import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as platformUtils from 'utils/platform';
import { useTranslation } from 'react-i18next';
import { Button, Popconfirm, Select, Tag, Tooltip, Spin, Modal, Radio, Dropdown } from 'antd';
import { connect } from 'react-redux';
import { downloadAsFile } from 'utils/file-utils';
import { EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import RestHelper from 'utils/RestHelper';
import { useZhEn } from 'utils/react-hooks/useZhEn';
import { isMobile, isPcOrLinux, isIos, isPc, PlatformUtil, isPlaystation, isHarmony, isAndroid } from 'utils/platform';
import { ServerAppSettings } from 'utils/server-app-settings';
import { ReportDetailUtil } from 'components/exception/issue/ReportDetail/ReportDetail';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import reportEvent, { EVENT_ACTIONS } from 'utils/reportEvent';
import StackIcon from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_stack_icon.svg';
import StackAi from 'svg/v2/newcs_aiassistant_aianalysis.svg';
import CsSwapSelect from 'components/commons/CsSwapSelect/CsSwapSelect';
import { CS_STYLES } from 'utils/constants/style-constants';
import scss from './DataHead.scss';
import { isZh, ze } from 'utils/zhEn';
import classnames from 'classnames';
import CsCollapseButton from 'components/commons/CsCollapseButton/CsCollapseButton';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';
import { DetailStackMemoryInfoUtil } from 'utils/detail-stack-memory-info-util';
import { isNotNullish, isNullish } from 'utils/nullish';
import RetraceHistoryModal, { RetraceHistoryUtil } from 'components/exception/issue/DataHead/RetraceHistoryModal';
import { ExceptionTypeInt } from 'utils/constants/exception-type-int';
import IssueJankFlamePanel from './IssueJankFlamePanel';
import NoticeIcon from 'svg/v2/newcs_dashboard_notice_icon.svg';
import IconDefinitive from 'svg/v2/newcs_dashboard_crashanalysis_issuelist_unfold_errorstack_mark.svg';
import CsSimpleButton from 'components/antd-extension/CsSimpleButton';
import DuibiIconNormal from 'svg/v2/newcs_dashboard_duibi_icon_normal.svg';
import { useCrashsightLocalStorage } from 'utils/useCrashSightLocalStorage';
import SubJankIssueFlameGraphModal
  from 'components/exception/ProductSummaryPage/TopIssueList/SubJankIssueFlameGraphModal';
import { useLocalStorageStateOfJankIssue } from 'utils/react-hooks/cs-local-storage-hooks';
import useZuStore from 'store/zustand/zustand-store';
import { makeDetailObjectFromStackLine } from 'utils/helper';
import { DocsUtil } from 'utils/docs-util';
import { shouldShowExpMessageClassifyMark, shouldShowExpNameClassifyMark } from 'utils/issue-classify-mark';

function noop(e) {
}

const MINIDUMP_STATUS = {
  PROCESS: 'PROCESS', // 还原中
  SUCCESS: 'SUCCESS', // 还原成功
  FAIL: 'FAIL', // 还原失败
};

const OtherThreadExtraInfoKeyList = [{
  key: 'stat',
  label: ze('读取/proc/[tid]/stat文件', 'Read /proc/[tid]/stat File'),
}, {
  key: 'wchan',
  label: ze('读取/proc/[tid]/wchan文件', 'Read /proc/[tid]/wchan File'),
}, {
  key: 'syscall',
  label: ze('读取/proc/[tid]/syscall文件', 'Read /proc/[tid]/syscall File'),
}, {
  key: 'stackUsed',
  label: ze('当前线程已用栈内存', 'Current Thread Used Stack Memory'),
}, {
  key: 'stackSize',
  label: ze('当前线程最大栈内存', 'Current Thread Max Stack Memory'),
}];

/**
 * 在点号或美元符号前面插入<wbr>标签以实现自动换行的组件
 * 用于处理类似Java包名等长文本的换行问题
 */
const BreakableText = React.memo(({ text }) => {
  if (!text) {
    return text;
  }

  // 使用正则表达式分割点号和美元符号，并保留分隔符
  const parts = text.split(/([\.$])/);

  return (
    <>
      {parts.map((part, index) => {
        // split保留分隔符后，奇数索引位置是分隔符，在前面插入<wbr>
        if (index % 2 === 1) {
          return (
            <React.Fragment key={index}>
              <wbr />
              {part}
            </React.Fragment>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
});

BreakableText.displayName = 'BreakableText';

BreakableText.propTypes = {
  text: PropTypes.string,
};

const DataHead = ({
  exceptionName, stackType, handleStackTypeClick, handleReloadCrash, style, height, onClick, pid, threadName, exceptionMessage,
  stackContent, issue, exceptionType, user, isIssueRegroup, actions, reduxState,
  threadNameLabel,
  otherThreadExtraInfo,
  shareId,symbolInfoLoading,isChildMethodExecuting,
  symbolInfoList,
  isMainThread,
  retraceLoading,
  retraceStatusQueryResult,
  isCollapsed,
  hideReretraceButton,
  stackTraceTypeSwapSelect,
  onClickRedirectToJankRawReport = () => {},
}) => {
  const {t} = useTranslation();
  const {ze} = useZhEn();
  const {isSuper} = (user && user.toJS()) || {};
  const appId = reduxState.app.get('current').get('appId');
  const demoApp = reduxState.app.get('current').get('demoApp');

  const aiDrawerSlice = useZuStore().aiDrawer;

  const issueObj = useMemo(() => {
    if (!issue) {
      return {};
    }
    return issue.toJS();
  }, [issue]);

  const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
  const enableErrorRetrace = serverAppSettings[ServerAppSettings.keys.enableErrorRetrace];
  const enableAiAnalyse = serverAppSettings[ServerAppSettings.keys.enableAiAnalyse];
  const pcClassify = serverAppSettings[ServerAppSettings.keys.pcClassify];
  const {needExpMessage, needExpName, needPcAddress} = pcClassify || {};
  const isPcResult = isPc(pid, appId);

  const enableAiStackAnalysis = enableAiAnalyse && !demoApp;

  const exceptionCategory = ExceptionCategoryUtil.unify(exceptionType);

  const isReretraceAvailableByExceptionType = ExceptionCategoryUtil.isCrash(exceptionCategory)
    || (ExceptionCategoryUtil.isError(exceptionCategory) && enableErrorRetrace)
    || (ExceptionCategoryUtil.isHang(exceptionCategory))
    || (ExceptionCategoryUtil.isAnr(exceptionCategory) && isHarmony(pid));

  // 重还原是否可用
  const isReretraceAvailable = !isIssueRegroup
    && !shareId
    && !hideReretraceButton
    && isReretraceAvailableByExceptionType;

  const shrinkFilePathOptions = [
    { label: ze('完整文件路径', 'Full File Path'), value: 'fullFilePath' },
    { label: ze('缩略文件路径', 'Short File Path'), value: 'shrunkFilePath' },
  ];

  const stackFilePathDisplayType = reduxState.global.get('localStorage').get('stackFilePathDisplayType') || 'fullFilePath';
  const [jankIssueStorage, setJankIssueStorage] = useLocalStorageStateOfJankIssue();

  const [miniDumpDemonstrate,setMiniDumpDemonstrate] = useState();
  const [miniDumpModal,setMiniDumpModal] = useState(false);

  const [retraceHistoryModalVisible, setRetraceHistoryModalVisible] = useState(false);

  const [subJankIssueFlameGraphModalVisible, setSubJankIssueFlameGraphModalVisible] = useState(false);

  const hasOtherThreadExtraInfo = !!otherThreadExtraInfo;
  const [isOtherThreadExtraInfoExpanded, setIsOtherThreadExtraInfoExpanded] = useState(false);

  const issueExceptionType = issue?.get('issueExceptionType');
  const isShowingMinidumpStack = issue?.get('isShowingMinidumpStack') || false;
  const isShowingLuaCppStack = issue?.get('isShowingLuaCppStack') || false;
  const luaLinesFragmentList = issue?.get('luaLinesFragmentList') || [];
  const hasLuaLines = luaLinesFragmentList.length > 0;
  const isShowingCsharpAndUsymStack = issue?.get('isShowingCsharpAndUsymStack') || false;
  const csharpAndUsymStack = issue?.get('csharpAndUsymStack') || '';
  const hasCsharpStack = !!csharpAndUsymStack;
  const hasUsymSymbol = useMemo(() => {
    return (symbolInfoList || []).some((x) => {
      const { uuid } = x;
      const isUsymSymbol = uuid?.startsWith('USym');
      return isUsymSymbol;
    });
  }, [symbolInfoList]);
  const detailStackMemoryInfoList = issue?.get('minidumpDetailStackMemoryInfoList') || [];
  const { jankRelatedReportEsMapList } = issueObj || {};
  const crashDoc = issue ? (issue.get('crashDoc') || {}) : {};
  const esMap = crashDoc.esMap || {};
  const crashMap = crashDoc.crashMap || {};
  const {
    issueId,
    crashId,
    crashExceptionType,
    retraceCrashDetail,
  } = crashMap;
  const {
    mainThreadName, // PS5需求，需要展示是否是主线程
    exceptionType: pcExpType,  // PC windbg的exceptionType没有写到expName上，所以要额外取一下
    hasMinidump,
    missingBuildId,
    minidumpParseStatus,
    minidumpParseFailReason,
    crashMinidumpStackCosKey,
  } = esMap;

  const isAndroidHasApkModule = useMemo(() => {
    if (!isAndroid(pid) || !retraceCrashDetail) {
      return false;
    }
    const stackLines = retraceCrashDetail.split('\n');
    const stackLineObjList = stackLines.map((stackLine, i) => {
      return makeDetailObjectFromStackLine(stackLine, exceptionCategory, pid);
    }).filter(x => x?.line);
    return stackLineObjList.some(x => x.libname?.toLowerCase()?.trim()?.endsWith('.apk'));
  }, [pid, exceptionCategory, retraceCrashDetail]);

  const isCrash = ExceptionCategoryUtil.isCrash(exceptionType);
  const isJankCategory = ExceptionCategoryUtil.isJank(exceptionType);
  const isIosPlatform = isIos(pid);

  // MetricKit 上报的异常消息不参与问题分类，需隐藏对应图标
  const metrickit = crashMap?.reservedMap?.metrickit;
  const isMetricKit = metrickit === true || (typeof metrickit === 'string' && metrickit === 'true');
  const reportType = crashMap?.type || esMap?.type || '';
  const classifyExpMessageEnable = typeof esMap?.classifyExpMessageEnable === 'boolean'
    ? esMap.classifyExpMessageEnable
    : (typeof crashMap?.classifyExpMessageEnable === 'boolean' ? crashMap.classifyExpMessageEnable : undefined);
  const showExpNameClassifyMark = shouldShowExpNameClassifyMark({
    isPcResult,
    needExpName,
    reportType,
    appId,
  });
  const showExpMessageClassifyMark = shouldShowExpMessageClassifyMark({
    pid,
    isPcResult,
    needExpMessage,
    isMetricKit,
    exceptionCategory,
    reportType,
    appId,
    expName: crashMap?.expName || exceptionName || '',
    expMessage: exceptionMessage || crashMap?.expMessage || '',
    callStack: retraceCrashDetail || crashMap?.callStack || '',
    classifyExpMessageEnable,
  });

  const crashedAsmLine = isCrash && (isPcOrLinux(pid) || isShowingMinidumpStack)
    ? DetailStackMemoryInfoUtil.getCrashedAsmLine(detailStackMemoryInfoList)
    : '';

  // 移动端、Linux 根据 SIGSEGV 判断是否展示崩溃地址（注：历史原因导致PC项目也放开，因为PC项目没有 SIGSEGV，故不会受影响）
  const isMobileOrLinuxShowExpAddr = (PlatformUtil.isMobileOrMobileLike(pid) || isPcOrLinux(pid) || isPlaystation(pid))
    && isCrash
    && (exceptionName || '').includes('SIGSEGV');
  // PC根据 EXCEPTION_ACCESS_VIOLATION_READ 等判断是否展示崩溃地址
  const isPcShowExpAddr = isPc(pid, appId)
    && isCrash
    && ['EXCEPTION_ACCESS_VIOLATION_READ', 'EXCEPTION_ACCESS_VIOLATION_WRITE'].includes(exceptionName);
  const isShowExpAddr = isMobileOrLinuxShowExpAddr || isPcShowExpAddr;

  let downloadMinidumpTooltip = ze('未触发过Minidump还原堆栈，或Minidump重还原请求尚未执行。', 'The minidump stack trace re-symbolication has not been triggered, or the minidump re-symbolication request has not been executed yet.');
  if (minidumpParseStatus === MINIDUMP_STATUS.PROCESS) {
    downloadMinidumpTooltip = ze('正在处理Minidump还原。', 'The minidump re-symbolication is processing.');
  } else if (minidumpParseStatus === MINIDUMP_STATUS.SUCCESS) {
    downloadMinidumpTooltip = ze('查看Minidump还原结果', 'View the minidump re-symbolication results');
  } else if (minidumpParseStatus === MINIDUMP_STATUS.FAIL) {
    downloadMinidumpTooltip = ze(`Minidump还原失败，错误码：${minidumpParseFailReason}`, `Minidump re-symbolication failed, error code: ${minidumpParseFailReason}`);
  }

  // props的exceptionName是issue的，只用于判断是否显示异常名字段，不用于展示内容，展示内容要取crash上的
  const crashExpName = isPcOrLinux(pid)
    ? (crashMap.expName || pcExpType || '')
    : (crashMap.expName || '');


  const { jankFlameGraphData, parseFailed } = useMemo(() => {
    let jankFlameGraphJson = null;

    if (crashExceptionType === ExceptionTypeInt.JankRaw) {
      jankFlameGraphJson = crashMap.jankFlameGraphJson;
    } else if (crashExceptionType === ExceptionTypeInt.JankParsed) {
      const relatedRawEsMap = (jankRelatedReportEsMapList || [])
        .find(esMap => esMap.crashExceptionType === ExceptionTypeInt.JankRaw);
      jankFlameGraphJson = relatedRawEsMap?.jankFlameGraphJson;
    }

    if (!jankFlameGraphJson) {
      return { jankFlameGraphData: null, parseFailed: false };
    }

    try {
      const jankFlameGraphData = JSON.parse(jankFlameGraphJson);
      return { jankFlameGraphData, parseFailed: false };
    } catch (e) {
      return { jankFlameGraphData: null, parseFailed: true };
    }
  }, [crashExceptionType, crashMap, jankRelatedReportEsMapList]);

  function onRetraceConfirm() {
    if (isShowingMinidumpStack) {
      handleStackTypeClick('retraceMinidump');
    } else {
      handleStackTypeClick('retraceAgain');
    }

    reportEvent({
      action: EVENT_ACTIONS.CLICK,
      tp1: '重还原堆栈',
    });
  }

  function onClickAiStackAnalysis() {
    // 堆栈分析：使用 crashId 作为 trigger，相同 crash 关闭后再打开保持状态
    aiDrawerSlice.startStackAnalysis({
      trigger: crashId ? `stack-${crashId}` : undefined,
    });
    // handleStackTypeClick('aiParsing');
  }

  async function restoreRetraceMinidumpConfirm(){
    const downloadUrl = `${crashMinidumpStackCosKey}.dmp.gz`;
    const data = await RestHelper.post('/api/crash/decompressInflateFile', {
      appId,
      platformId:pid,
      cosKey:downloadUrl,
      compressFormat: 'GZIP',
    });
    const { hasFile, contentBase64 } = data.json.data;
    if (hasFile) {
      const pcLogUint8Array = Uint8Array.from(window.atob(contentBase64), c => c.charCodeAt(0));
      const pcLogText = new TextDecoder().decode(pcLogUint8Array);
      setMiniDumpDemonstrate(pcLogText);
    }else{
      setMiniDumpDemonstrate('minidump还原堆栈结果为空');
    }
    setMiniDumpModal(true);
  }

  const polishedExceptionMessage = useMemo(() => {
    return (exceptionMessage || '')
      .split('\n')
      .filter(x => x.trim())
      .join('\n');
  }, [exceptionMessage]);

  const {
    retraceStatus,
    lastRetraceStartTime,
    lastRetraceEndTime,
  } = retraceStatusQueryResult || {};

  let lastRetraceStatusDom;
  if (isNotNullish(retraceStatus) && retraceStatus !== 0) {
    const text = RetraceHistoryUtil.getTextByRetraceStatus(retraceStatus);
    const styleObject = RetraceHistoryUtil.getStyleObjectByRetraceStatus(retraceStatus);
    lastRetraceStatusDom = <div style={styleObject}>{ ze(`（${text}）`, `(${text})`) }</div>
  }

  const isRetraceProcessing = retraceLoading;

  const missingBuildUrl = `https://gitee.com/openharmony/docs/blob/master/${isZh() ? 'zh-cn' : 'en'}/application-dev/dfx/hiappevent-watcher-freeze-events.md`;

  // PS5需求，需要展示是否是主线程
  const isCrashedOnMainThread = !!mainThreadName && mainThreadName === threadName;

  function renderOtherThreadExtraInfo() {
    if (!hasOtherThreadExtraInfo || !isOtherThreadExtraInfoExpanded) {
      return null;
    }
    const rows = OtherThreadExtraInfoKeyList.map(x => {
      const { key, label } = x;
      const v = otherThreadExtraInfo[key];
      if (!v) {
        return null;
      }
      return <div style={{ display: 'flex', gap: '8px' }}>
        <div className={scss.fieldName}>{ label }</div>
        <div className={scss.fieldValueWrap}>{ v }</div>
      </div>;
    }).filter(x => x);

    return <div>{ rows }</div>;
  }

  return (
    <div>
      <div
        className={classnames(style.data_head, scss.dataHead, !isMainThread ? scss.otherThread : '' )}
        style={{ alignItems: 'center' }}
        onClick={onClick || noop}
      >
        <div style={{ marginRight: '24px' }}><StackIcon /></div>
        { (!!exceptionName || !!exceptionMessage || !!threadName)
        && (
        <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: '16px', overflowWrap:'break-word'}}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '4px', alignItems:'center' }}>
              <div className={scss.fieldName}>{threadNameLabel}</div>
              <div className={scss.fieldValueNoWrap}><BreakableText text={threadName} /></div>
              { isCrashedOnMainThread && <Tag color='processing'>{ ze('主线程', 'Main Thread') }</Tag> }
            </div>
            {exceptionName && <div style={{ display: 'flex', gap: '4px', alignItems:'center' }}>
              {showExpNameClassifyMark && <Tooltip
                title={<div>
                  <div>{ze('该异常名应用于问题分类。','Exception name used for issue grouping.')}</div>
                </div>}
              ><IconDefinitive/></Tooltip>}
              <div className={scss.fieldName}>{t('REPORTDETAIL.expName')}: </div>
              <div className={scss.fieldValueNoWrap}><BreakableText text={isIosPlatform && isCrash && polishedExceptionMessage ? `${crashExpName}(${polishedExceptionMessage})` : crashExpName} /></div>
            </div>}
            { hasOtherThreadExtraInfo && <Button
              size='small'
              onClick={(e) => {
                setIsOtherThreadExtraInfoExpanded(!isOtherThreadExtraInfoExpanded);
                e.stopPropagation();
              }}
            >{ ze(`${ isOtherThreadExtraInfoExpanded ? '收起' : '展开' }线程详情`, `${ isOtherThreadExtraInfoExpanded ? 'Expand' : 'Collapse'} Detail`) }</Button> }
          </div>
          {exceptionMessage && <div>
            <div style={{ display: 'flex', gap: '4px', alignItems:'center' }}>
            {showExpMessageClassifyMark && <Tooltip
                title={<div>
                  <div>{ze('该异常消息应用于问题分类。','Error Message used for issue grouping.')}</div>
                </div>}
              ><IconDefinitive/></Tooltip>}
              <div className={scss.fieldName}>{ t('issueCrashFilterKey.crashExpMessage') }:</div>
              <div className={scss.fieldValueWrap}>{ polishedExceptionMessage }</div>
            </div>
          </div> }
          { isShowExpAddr && <div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div className={scss.fieldName}>{ ze('崩溃时访问地址', 'Accessed Address When Crashed') }:</div>
              <div className={scss.fieldValueNoWrap}>{ReportDetailUtil.renderExpAddr(crashMap.expAddr)}</div>
            </div>
          </div> }
          { crashedAsmLine && <div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div className={scss.fieldName}>{ ze('崩溃时执行的汇编', 'ASM Executed When Crashed') }:</div>
              <div className={scss.fieldValueNoWrap}>{ crashedAsmLine }</div>
            </div>
          </div> }
          { hasOtherThreadExtraInfo && isOtherThreadExtraInfoExpanded && renderOtherThreadExtraInfo() }
        </div>
        ) }
        { !isMainThread && stackContent && <CsCollapseButton
          isSmallSize={true}
          isCollapsed={isCollapsed}
          enableHover={true}
        /> }
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          { isMainThread && stackTraceTypeSwapSelect && <div style={{ display: 'flex', justifyContent: 'end' }}>
            { stackTraceTypeSwapSelect }
          </div> }
          <div style={{ display: 'flex', gap: '8px' }}>
            { hasLuaLines && <CsSwapSelect
              type='primary'
              options={[{ value: 0, label: ze('切换为Lua/C++混合堆栈', 'Switch To Lua/C++ Mixed Stack') }, { value: 1, label: ze('切换为C++堆栈', 'Switch To C++ Stack') }]}
              value={Number(!!isShowingLuaCppStack)}
              onChange={(v) => {
                actions.patchCurrentIssueInfo({
                  isShowingLuaCppStack: !!v,
                });
              }}
            /> }
            { hasCsharpStack && <Tooltip
              title={!hasUsymSymbol ? <div>
                <span>{ze('需要上传Usym符号文件才可查看C#/C++混合堆栈。详情可参考', 'Please upload USym symbol files to view C#/C++ mixed stack trace. For more details, please refer to ')}</span>
                <a
                  href={DocsUtil.makeDocsUrl('/sdkDocuments/unity-sdk-new/#configuring-usym-symbolication')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {ze('配置Usym还原', 'Configure Usym Symbolication')}
                </a>
              </div> : undefined}
            >
              <div><CsSwapSelect
                disabled={!hasUsymSymbol}
                type='primary'
                options={[{ value: 0, label: ze('切换为C#/C++混合堆栈', 'Switch To C#/C++ Mixed Stack') }, { value: 1, label: ze('切换为C++堆栈', 'Switch To C++ Stack') }]}
                value={Number(!!isShowingCsharpAndUsymStack)}
                onChange={(v) => {
                  actions.patchCurrentIssueInfo({
                    isShowingCsharpAndUsymStack: !!v,
                  });
                }}
              /></div>
            </Tooltip> }
            {
              isMainThread && isReretraceAvailable
              && <div onClick={(e) => e.stopPropagation()}>
                <Popconfirm
                  disabled={isRetraceProcessing}
                  title={t('ERRORSTACKTAB.重还原二次确认')}
                  onConfirm={onRetraceConfirm}
                >
                  <Button
                    disabled={isRetraceProcessing}
                    type='primary'
                    onClick={(e) => e.stopPropagation()}
                  >{isRetraceProcessing
                    ? <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><LoadingOutlined/>{ t("ERRORSTACKTAB.restoreTip") }</div>
                    : (isMainThread ? t('ERRORSTACKTAB.restore') : t('ERRORSTACKTAB.restoreOtherStack'))}</Button>
                </Popconfirm>
              </div>
            }
            {
              isMainThread && !ExceptionCategoryUtil.isJank(exceptionType) && <Select
                style={{ width: '150px' }}
                value={stackFilePathDisplayType}
                options={shrinkFilePathOptions}
                onChange={(v) => {
                  actions.setPartialLocalStorage({
                    stackFilePathDisplayType: v,
                  });
                }}
              />
            }
            {
              !shareId && isMainThread && Number(issueExceptionType) === ExceptionTypeInt.JankParsed && <Button
                onClick={() => onClickRedirectToJankRawReport()}
              >{ ze('查看原始上报', 'Open Raw Report') }</Button>
            }
            {
              isReretraceAvailable
              && isMainThread && (
                <Select
                  style={{ width: '150px' }}
                  options={[
                    { label: t('ERRORSTACKTAB.parse'), value: 'retrace' },
                    { label: t('ERRORSTACKTAB.origin'), value: 'original' },
                  ]}
                  value={stackType === 'original' ? 'original' : 'retrace'}
                  onChange={v => { handleStackTypeClick(v)} }
                />
              )}
            {
              isMainThread && enableAiStackAnalysis && <div style={{ display: 'flex' }}>
                <Spin spinning={isChildMethodExecuting}>
                  <Tooltip title={ze('AI堆栈分析', 'Analyze Stack Trace by AI')}>
                  <Button
                    style={{
                      width:'32px',
                      height:'32px',
                      paddingTop:'6px',
                    }}
                    onClick={() => onClickAiStackAnalysis()}
                    icon={<StackAi />}
                  >
                  </Button>
                  </Tooltip>
                </Spin>
              </div>
            }
          </div>
          { isMainThread && isNotNullish(retraceStatus) && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
            <div>{ ze('最近一次重试还原堆栈：', 'Recently re-symbolicated at: ') }{ lastRetraceStartTime || 'Unknown Time' }</div>
            { lastRetraceStatusDom }
            { /* <Button
              onClick={() => setRetraceHistoryModalVisible(true)}
              type='link'
              style={{ marginLeft: '12px', padding: 0, height: 'inherit' }}
            ><div>{ ze('查看历史', 'Show All') }</div></Button> */}
          </div> }
        </div>
        <RetraceHistoryModal
          visible={retraceHistoryModalVisible}
          onClose={() => setRetraceHistoryModalVisible(false)}
          appId={appId}
          issueId={issueId}
          crashId={crashId}
        />
      </div>
      {missingBuildId && <div className={scss.missingBuild}>
        <div style={{transform: 'rotate(180deg)', display: 'flex', alignItems:'center'}}><NoticeIcon /></div>
        <span>{ ze('在整机高负载的情况下，采用低开销方式获取调用栈的情况，损失了build-id信息', 'When the device is heavily loaded and the call stack is obtained in low-overhead mode, the buildId information may be lost.') }</span>
        <a
          target='_blank'
          rel='noreferrer noopener'
          href={missingBuildUrl}
        >
          {ze('详情', 'Details')}
        </a>
      </div>}
      { isAndroidHasApkModule && <div className={scss.hasApkModule}>
        <div style={{transform: 'rotate(180deg)', display: 'flex', alignItems:'center'}}><NoticeIcon /></div>
        <div>
          <span>{ ze('未提取的native库会导致获取的崩溃堆栈无法还原，表现为堆栈中的崩溃模块是.apk而非.so。请', 'Unextracted native libraries will cause the obtained crash stack to be unable to symbolicate, crash modules in the stack trace will be .apk instead of .so. Please ') }</span>
          <a
            target='_blank'
            rel='noreferrer noopener'
            href={DocsUtil.makeDocsUrl('/changelog')}
          >
            {ze('升级4.3.8以上版本的SDK', 'upgrade to SDK version 4.3.8 or above')}
          </a>
          <span>{ ze('，或者', ', or ') }</span>
          <a
            target='_blank'
            rel='noreferrer noopener'
            href={DocsUtil.makeDocsUrl('/sdkDocuments/mobile-sdk#configure-native-library-extraction')}
          >
            {ze('配置native库提取', 'configure native library extraction')}
          </a>
          <span>{ ze('。', '.') }</span>
        </div>
      </div> }
      {isJankCategory && isNotNullish(jankFlameGraphData) && <div
        style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '8px' }}
      >
        <Dropdown
          menu={{
            items: [
              { label: ze('添加为基准火焰图', 'Add as Base Issue'), key: 'ADD_BASE_JANK_FLAME_GRAPH' },
              { label: ze('添加为对比火焰图', 'Add as Compare Issue'), key: 'ADD_COMPARE_JANK_FLAME_GRAPH' },
            ],
            onClick({ key: op }) {
              setJankIssueStorage(prev => {
                if (op === 'ADD_BASE_JANK_FLAME_GRAPH') {
                  return {
                    ...prev,
                    appId,
                    baseIssueId: issueId,
                  };
                } else if (op === 'ADD_COMPARE_JANK_FLAME_GRAPH') {
                  return {
                    ...prev,
                    appId,
                    compareIssueId: issueId,
                  };
                }
              });
              setSubJankIssueFlameGraphModalVisible(true);
            },
          }}
        >
          <div><CsSimpleButton
            style={{ padding: '4px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px'}}>
              <div>{ ze('火焰图对比', 'Flame Graph Comparison') }</div>
              <DuibiIconNormal />
            </div>
          </CsSimpleButton></div>
        </Dropdown>
      </div>}
      {isJankCategory && <div>
        {(isNotNullish(jankFlameGraphData) || parseFailed) && <IssueJankFlamePanel
          singleTree={jankFlameGraphData}
          parseFailed={parseFailed}
          appId={appId}
          pid={pid}
          issueId={issueId}
          crashId={crashId}
          parsedJank={crashExceptionType === ExceptionTypeInt.JankParsed}
        />}
        { <SubJankIssueFlameGraphModal
          visible={subJankIssueFlameGraphModalVisible}
          onClose={() => setSubJankIssueFlameGraphModalVisible(false)}
          isIssueDetailCompareMode={true}
        /> }
      </div>}
    </div>
  );
};

DataHead.propTypes = {
  reduxState: PropTypes.object,
  style: PropTypes.object.isRequired,
  exceptionName: PropTypes.string,
  handleStackTypeClick: PropTypes.func,
  stackType: PropTypes.string,
  height: PropTypes.string,
  onClick: PropTypes.func,
  pid: PropTypes.string,
  threadName: PropTypes.string,
  exceptionMessage: PropTypes.string,
  stackContent: PropTypes.string,
  issue: PropTypes.object,
  exceptionType: PropTypes.string,
  user: PropTypes.object,
  threadNameLabel: PropTypes.string,
  actions: PropTypes.object,
  // 表示是否是堆栈重分类界面嵌入的lastReport，这个界面嵌入的lastReport有些功能是不提供的，同时额外提供指定堆栈功能
  isIssueRegroup: PropTypes.bool,
  shareId: PropTypes.string,  // 分享链接的id，如果不为空表示是通过分享链接查看的
  symbolInfoLoading: PropTypes.string,
  // 符号表信息，用于堆栈上提示是否缺符号表
  symbolInfoList: PropTypes.array,
  isMainThread: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  hideReretraceButton: PropTypes.bool,
};

const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(DataHead);
